/**
 * ALTIORA Design System — Tokens
 *
 * Single source of truth for every visual dimension of the product.
 * NEVER hard-code a hex, spacing or radius elsewhere. Always import
 * from here so a change in one place propagates everywhere.
 */

// ── Colours ────────────────────────────────────────────────────────
export const COLOR = {
  navy:        "#0B2545",
  navyDark:    "#061528",
  champagne:   "#C5A880",
  champagneDark: "#B79768",
  gold:        "#F5D687",
  ivory:       "#FBF6EA",
  ivoryAlt:    "#F1EFE9",
  white:       "#FFFFFF",
  cream:       "#FBF6EA",

  slate50:     "#F8FAFC",
  slate100:    "#F1F5F9",
  slate200:    "#E5E7EB",
  slate400:    "#94A3B8",
  slate600:    "#475569",
  slate700:    "#334155",
  slate900:    "#0F172A",

  green:       "#0B7A3B",   // semantic OK
  greenLeaf:   "#7EE0AA",   // decorative
  amber:       "#B45309",   // warning
  amberLight:  "#FEF3C7",
  red:         "#B91C1C",   // danger
  redLeaf:     "#FCA5A5",
  redLight:    "#FEF2F2",
  info:        "#0369A1",
  infoLight:   "#E0F2FE",
};

// Semantic aliases
export const SEMANTIC = {
  bg:         COLOR.ivory,
  bgAlt:      COLOR.ivoryAlt,
  surface:    COLOR.white,
  border:     "#E5E7EB",
  borderSoft: `${COLOR.champagne}22`,
  text:       COLOR.navy,
  textMuted:  COLOR.slate400,
  textStrong: COLOR.navyDark,
  primary:    COLOR.champagne,
  onPrimary:  COLOR.navyDark,
  accent:     COLOR.gold,
  success:    COLOR.green,
  warning:    COLOR.amber,
  danger:     COLOR.red,
  focusRing:  COLOR.navy,
};

// ── Typography ─────────────────────────────────────────────────────
export const TYPO = {
  // Font families come from tailwind base — serif = display, sans = body
  familySerif: `'Playfair Display', 'Georgia', 'Cambria', serif`,
  familySans:  `system-ui, -apple-system, 'Inter', 'Helvetica Neue', sans-serif`,
  familyMono:  `'JetBrains Mono', ui-monospace, SFMono-Regular, monospace`,

  size: {
    xs:    "0.6875rem",  // 11px — overlines, hints
    sm:    "0.8125rem",  // 13px — captions
    base:  "0.9375rem",  // 15px — body
    md:    "1rem",       // 16px — comfortable body
    lg:    "1.125rem",   // 18px — lead paragraph
    xl:    "1.375rem",   // 22px — h5
    "2xl": "1.75rem",    // 28px — h3
    "3xl": "2.25rem",    // 36px — h2
    "4xl": "clamp(2rem, 4vw, 3.6rem)",       // h1 responsive
    "5xl": "clamp(2.5rem, 6vw, 5.5rem)",     // hero
  },
  tracking: {
    tight:  "-0.01em",
    normal: "0",
    wide:   "0.04em",
    over:   "0.28em",   // signature landing overline
    over2:  "0.32em",
    over3:  "0.4em",
  },
  weight: {
    regular: 400, medium: 500, semibold: 600, bold: 700,
  },
  lineHeight: {
    tight: 1.02, snug: 1.15, normal: 1.5, relaxed: 1.65,
  },
};

// ── Spacing scale (rem) ────────────────────────────────────────────
export const SPACE = {
  0:  "0",
  0.5:"0.125rem",
  1:  "0.25rem",  2:  "0.5rem",
  3:  "0.75rem",  4:  "1rem",
  5:  "1.25rem",  6:  "1.5rem",
  8:  "2rem",     10: "2.5rem",
  12: "3rem",     14: "3.5rem",
  16: "4rem",     20: "5rem",
  24: "6rem",     32: "8rem",
};

// ── Radii ──────────────────────────────────────────────────────────
export const RADIUS = {
  none: "0",
  sm:   "0.25rem",   // 4px
  md:   "0.5rem",    // 8px  — inputs, chips
  lg:   "0.75rem",   // 12px — buttons
  xl:   "1rem",      // 16px — cards
  "2xl":"1.25rem",   // 20px — panels
  full: "9999px",    // pill
};

// ── Shadows ────────────────────────────────────────────────────────
export const SHADOW = {
  none:      "none",
  xs:        "0 1px 2px -1px rgba(11,37,69,0.06)",
  sm:        "0 2px 8px -3px rgba(11,37,69,0.06)",
  md:        "0 8px 24px -12px rgba(11,37,69,0.12)",
  lg:        "0 10px 40px -20px rgba(11,37,69,0.20)",
  xl:        "0 24px 60px -24px rgba(11,37,69,0.24)",
  focus:     `0 0 0 3px ${COLOR.navy}22`,
  goldGlow:  `0 0 0 3px ${COLOR.champagne}33`,
};

// ── Z-index ────────────────────────────────────────────────────────
export const Z = {
  base: 0, dropdown: 40, sticky: 50, drawer: 60,
  modal: 70, popover: 80, toast: 90, tooltip: 100,
};

// ── Breakpoints (px) — match Tailwind defaults ─────────────────────
export const BP = {
  sm: 640, md: 768, lg: 1024, xl: 1280, "2xl": 1536,
};
